import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BadgeCheck, X, MapPin, Flag, Ban, RotateCcw, ScrollText, CalendarHeart, Trash2, Plus, Users,
  LayoutDashboard, LifeBuoy, UserCog, Search, Send, ShieldAlert, TrendingUp, UserPlus, Ticket, Sparkles, ShieldCheck,
  Gift, Clock, Wallet, Eye, UserCheck, CheckSquare, Square, MessageSquare, Radio, Crown, Mail, Share2,
} from "lucide-react";
import { ScheduledBroadcasts } from "@/components/ScheduledBroadcasts";
import { SocialHub } from "@/components/SocialHub";
import { StripeStatus } from "@/components/StripeStatus";
import { AgeReviews } from "@/components/AgeReviews";
import { CreatorApplications } from "@/components/CreatorApplications";
import { EmailPreviews } from "@/components/EmailPreviews";
import { MarketingAdmin } from "@/components/MarketingAdmin";
import { WalletReversals } from "@/components/WalletReversals";
import { ModelsList } from "@/components/ModelsList";
import { Megaphone } from "lucide-react";
import { MyAds } from "@/components/MyAds";
import { dollars } from "@/lib/giftIcons";

const FALLBACK = "https://images.unsplash.com/photo-1637059880830-59a90102de77";

function UserDetailModal({ data, onClose }) {
  if (!data) return null;
  const u = data.user;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="user-detail-modal" className="w-full max-w-2xl my-10 rounded-3xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-5">
          <img src={u.photo_url || FALLBACK} alt="" className="w-20 h-20 rounded-2xl object-cover" />
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-3xl leading-none">{u.display_name}<span className="text-text-muted text-lg">, {u.age}</span></h2>
            <p className="text-xs text-text-muted mt-2 truncate">{u.email}{u.location ? ` · ${u.location}` : ""}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`text-[10px] uppercase tracking-[0.12em] border px-2.5 py-1 rounded-full ${TIER_STYLE[u.membership_tier]}`}>{u.membership_tier}</span>
              {u.age_verified && <span className="text-[10px] uppercase tracking-[0.12em] border border-gold/40 text-gold px-2.5 py-1 rounded-full flex items-center gap-1"><ShieldCheck size={11} /> 18+</span>}
              {u.is_creator && <span className="text-[10px] uppercase tracking-[0.12em] border border-wine/40 text-gold px-2.5 py-1 rounded-full">Creator</span>}
              {u.suspended && <span className="text-[10px] uppercase tracking-[0.12em] border border-amber-500/40 text-amber-300 px-2.5 py-1 rounded-full">Suspended</span>}
              {u.warnings > 0 && <span className="text-[10px] uppercase tracking-[0.12em] border border-amber-500/40 text-amber-300 px-2.5 py-1 rounded-full">{u.warnings} warning{u.warnings > 1 ? "s" : ""}</span>}
            </div>
          </div>
          <button data-testid="user-detail-close" onClick={onClose} className="text-text-muted hover:text-text-main"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <div className="rounded-xl border border-white/8 p-3 text-center"><p className="font-display text-2xl">{dollars(u.wallet_balance)}</p><p className="text-[10px] uppercase tracking-[0.12em] text-text-muted mt-1">Wallet</p></div>
          <div className="rounded-xl border border-white/8 p-3 text-center"><p className="font-display text-2xl">{dollars(u.earnings_balance)}</p><p className="text-[10px] uppercase tracking-[0.12em] text-text-muted mt-1">Earnings</p></div>
          <div className="rounded-xl border border-white/8 p-3 text-center"><p className="font-display text-2xl">{data.gifts.received_count}</p><p className="text-[10px] uppercase tracking-[0.12em] text-text-muted mt-1">Gifts in</p></div>
          <div className="rounded-xl border border-white/8 p-3 text-center"><p className="font-display text-2xl">{data.reports.length}</p><p className="text-[10px] uppercase tracking-[0.12em] text-text-muted mt-1">Reports</p></div>
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-[0.15em] text-text-muted mb-3 flex items-center gap-2"><Gift size={13} /> Recent gifts ({dollars(data.gifts.received_total)} received)</p>
          {data.gifts.recent.length === 0 ? <p className="text-sm text-text-muted italic">No gift activity.</p> :
           <div className="space-y-2">
             {data.gifts.recent.map((g, i) => (
               <div key={i} className="flex items-center justify-between text-sm">
                 <span>{g.direction === "sent" ? "Sent" : "Received"} <span className="text-gold">{g.gift_name}</span> {g.direction === "sent" ? "to" : "from"} {g.with}</span>
                 <span className="text-text-muted">{dollars(g.amount)}</span>
               </div>
             ))}
           </div>}
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-[0.15em] text-text-muted mb-3 flex items-center gap-2"><Clock size={13} /> Private sessions</p>
          {data.sessions.length === 0 ? <p className="text-sm text-text-muted italic">No private sessions.</p> :
           <div className="space-y-2">
             {data.sessions.map((sn, i) => (
               <div key={i} className="flex items-center justify-between text-sm">
                 <span>As <span className="text-gold">{sn.role}</span> with {sn.with} · {sn.minutes} min</span>
                 <span className="text-text-muted">{dollars(sn.total)} · {sn.status}</span>
               </div>
             ))}
           </div>}
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-[0.15em] text-text-muted mb-3 flex items-center gap-2"><Flag size={13} /> Reports against this member</p>
          {data.reports.length === 0 ? <p className="text-sm text-text-muted italic">No reports.</p> :
           <div className="space-y-2">
             {data.reports.map((r, i) => (
               <div key={i} className="flex items-center justify-between text-sm">
                 <span className="text-text-main/85">{r.reason || "No reason given"}</span>
                 <span className={`text-[10px] uppercase tracking-[0.12em] ${r.resolved ? "text-emerald-300" : "text-amber-300"}`}>{r.resolved ? "resolved" : "open"}</span>
               </div>
             ))}
           </div>}
        </div>
      </div>
    </div>
  );
}

function WarnModal({ open, name, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="warn-modal" className="w-full max-w-md rounded-3xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
        <p className="flex items-center gap-2 text-amber-300 text-sm uppercase tracking-[0.15em] mb-4"><ShieldAlert size={16} /> Warn {name}</p>
        <textarea data-testid="warn-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
          placeholder="Explain the guideline violation. This is sent to the member." className="w-full bg-transparent border border-white/15 rounded-2xl p-4 focus:border-amber-500/50 outline-none transition-colors" />
        <div className="flex gap-3 mt-5">
          <button data-testid="warn-cancel" onClick={onClose} className="flex-1 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main transition-colors">Cancel</button>
          <button data-testid="warn-confirm" disabled={reason.trim().length < 2} onClick={() => onConfirm(reason)}
            className="flex-1 py-3 rounded-full bg-amber-600 hover:bg-amber-700 transition-colors text-xs uppercase tracking-[0.15em] disabled:opacity-40">Send warning</button>
        </div>
      </div>
    </div>
  );
}

function MessageModal({ target, onClose, onSend }) {
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);
  const [when, setWhen] = useState("now");
  const [previewing, setPreviewing] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [repeat, setRepeat] = useState("none");
  if (!target) return null;
  const broadcast = target.broadcast;
  const scheduled = broadcast && when === "later";
  const submit = async () => {
    setSending(true);
    try { await onSend(body.trim(), audience, scheduled ? { send_at: new Date(sendAt).toISOString(), repeat } : null); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="message-modal" className="w-full max-w-lg rounded-3xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
        <p className="flex items-center gap-2 text-gold text-sm uppercase tracking-[0.15em] mb-1">
          {broadcast ? <Radio size={16} /> : <MessageSquare size={16} />} {broadcast ? "Broadcast to members" : `Message ${target.name}`}
        </p>
        <p className="text-xs text-text-muted mb-4">Delivered to {broadcast ? "each inbox" : "their inbox"} as a <span className="text-gold">Velluto Admin</span> system message.</p>
        {broadcast && (
          <select data-testid="broadcast-audience" value={audience} onChange={(e) => setAudience(e.target.value)}
            className="w-full mb-3 bg-black/30 border border-white/15 rounded-2xl px-4 py-3 text-sm outline-none focus:border-gold/50">
            <option value="all">All registered members</option>
            <option value="creators">Creators only</option>
            <option value="premium">Premium members</option>
            <option value="basic">Basic members</option>
            <option value="free">Free members</option>
          </select>
        )}
        {broadcast && (
          <div className="mb-3 rounded-2xl border border-white/10 bg-black/20 p-3">
            <div className="flex gap-2">
              {[["now", "Send now"], ["later", "Schedule"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`broadcast-when-${v}`} onClick={() => setWhen(v)}
                  className={`flex-1 py-2 rounded-full text-[10px] uppercase tracking-[0.15em] border transition-colors ${when === v ? "border-gold text-gold bg-gold/10" : "border-white/15 text-text-muted hover:text-text-main"}`}>{l}</button>
              ))}
            </div>
            {scheduled && (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_150px] gap-2 mt-3">
                <input data-testid="broadcast-send-at" type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                  min={new Date(Date.now() + 120000).toISOString().slice(0, 16)}
                  className="bg-black/30 border border-white/15 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-gold/50 [color-scheme:dark]" />
                <select data-testid="broadcast-repeat" value={repeat} onChange={(e) => setRepeat(e.target.value)}
                  className="bg-black/30 border border-white/15 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-gold/50">
                  <option value="none">Once</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                </select>
              </div>
            )}
          </div>
        )}
        <textarea data-testid="message-body" rows={5} value={body} onChange={(e) => setBody(e.target.value)} autoFocus maxLength={2000}
          placeholder={broadcast ? "Write the announcement every member will receive…" : "Write your message…"}
          className="w-full bg-transparent border border-white/15 rounded-2xl p-4 focus:border-gold/50 outline-none transition-colors" />
        {broadcast && body.trim() && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">How members will see it</p>
              <button type="button" data-testid="broadcast-preview-send" disabled={previewing} onClick={async () => {
                setPreviewing(true);
                try { await api.post("/admin/broadcast/preview", { body: body.trim() }); toast.success("Test copy delivered to your own inbox"); }
                catch (e) { toast.error(e.response?.data?.detail || "Could not send preview"); } finally { setPreviewing(false); }
              }} className="text-[10px] uppercase tracking-[0.15em] text-gold hover:text-gold/80 disabled:opacity-40">{previewing ? "Sending…" : "Send test to me"}</button>
            </div>
            <div data-testid="broadcast-preview" className="rounded-2xl border border-gold/40 bg-gold/8 px-5 py-3.5 max-w-[80%]">
              <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gold mb-1.5"><Sparkles size={11} /> Velluto Admin</p>
              <p className="text-sm whitespace-pre-wrap">{body}</p>
            </div>
          </div>
        )}
        <div className="flex gap-3 mt-5">
          <button data-testid="message-cancel" onClick={onClose} className="flex-1 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main transition-colors">Cancel</button>
          <button data-testid="message-send" disabled={body.trim().length < 1 || sending || (scheduled && !sendAt)} onClick={submit}
            className="flex-1 py-3 rounded-full bg-wine hover:bg-wine/80 transition-colors text-xs uppercase tracking-[0.15em] disabled:opacity-40">
            {sending ? "Sending…" : scheduled ? "Schedule broadcast" : broadcast ? "Send broadcast" : "Send message"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompForm({ onGranted }) {
  const [form, setForm] = useState({ email: "", tier: "premium", months: "0" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/complimentary", { email: form.email.trim(), tier: form.tier, months: parseInt(form.months, 10) });
      toast.success(`${data.display_name} now has complimentary ${form.tier}`);
      setForm({ email: "", tier: "premium", months: "0" });
      onGranted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not grant membership");
    } finally { setSaving(false); }
  };
  const field = "bg-black/30 border border-white/15 rounded-2xl px-4 py-3 text-sm outline-none focus:border-gold/50 transition-colors";
  return (
    <form data-testid="comp-form" onSubmit={submit} className="rounded-2xl border border-gold/25 bg-surface p-6">
      <p className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-1"><Crown size={14} /> Grant complimentary membership</p>
      <p className="text-xs text-text-muted mb-5">Gift a member free Basic or Premium access by email. They receive a branded email + Concierge DM; timed grants auto-revert when they expire.</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_170px_auto] gap-3">
        <input data-testid="comp-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="member@email.com" className={field} />
        <select data-testid="comp-tier" value={form.tier} onChange={(e) => set("tier", e.target.value)} className={field}>
          <option value="premium">Premium</option>
          <option value="basic">Basic</option>
        </select>
        <select data-testid="comp-months" value={form.months} onChange={(e) => set("months", e.target.value)} className={field}>
          <option value="0">Unlimited</option>
          <option value="1">1 month</option>
          <option value="3">3 months</option>
          <option value="6">6 months</option>
          <option value="12">12 months</option>
        </select>
        <button data-testid="comp-submit" disabled={saving} className="px-6 py-3 rounded-full bg-gold text-black text-xs uppercase tracking-[0.15em] hover:bg-gold/90 transition-colors disabled:opacity-40">
          {saving ? "Granting…" : "Grant"}
        </button>
      </div>
    </form>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-surface p-6">
      <div className="flex items-center justify-between">
        <span className={`w-10 h-10 rounded-full flex items-center justify-center ${accent}`}><Icon size={18} /></span>
        <span data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="font-display text-4xl font-light">{value}</span>
      </div>
      <p className="text-xs uppercase tracking-[0.15em] text-text-muted mt-4">{label}</p>
    </div>
  );
}

const TIER_STYLE = {
  premium: "text-gold border-gold/40", basic: "text-emerald-300 border-emerald-500/40", free: "text-text-muted border-white/15",
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [reqs, setReqs] = useState(null);
  const [reports, setReports] = useState(null);
  const [audit, setAudit] = useState(null);
  const [events, setEvents] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [userList, setUserList] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [warnTarget, setWarnTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msgTarget, setMsgTarget] = useState(null);
  const [schedKey, setSchedKey] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const emptyEv = { title: "", description: "", location: "", starts_at: "", cover_url: "", capacity: "" };
  const [evForm, setEvForm] = useState(emptyEv);
  const [savingEv, setSavingEv] = useState(false);

  const loadOverview = () => api.get("/admin/overview").then(({ data }) => setOverview(data));
  const loadReqs = () => api.get("/admin/verifications").then(({ data }) => setReqs(data));
  const loadReports = () => api.get("/admin/reports").then(({ data }) => setReports(data));
  const loadAudit = () => api.get("/admin/audit").then(({ data }) => setAudit(data));
  const loadEvents = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  const loadTickets = (opts = {}) => api.get("/admin/tickets", { params: { search: opts.search ?? ticketSearch, status: opts.status ?? ticketStatus } }).then(({ data }) => setTickets(data));
  const loadUsers = () => api.get("/admin/users", { params: { search: userSearch, kind: "members" } }).then(({ data }) => setUserList(data));

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    if (tab === "overview") loadOverview();
    else if (tab === "verifications") loadReqs();
    else if (tab === "reports") loadReports();
    else if (tab === "events") loadEvents();
    else if (tab === "tickets") loadTickets();
    else if (tab === "users") loadUsers();
    else if (tab === "audit") loadAudit();
  }, [user, tab]);

  if (user === null) return <div className="pt-32 text-center text-text-muted">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/browse" replace />;

  const actVerify = async (id, action) => {
    await api.post(`/admin/verifications/${id}/${action}`);
    toast.success(action === "approve" ? "Verified" : "Rejected");
    setReqs((r) => r.filter((x) => x.id !== id));
  };

  const actReport = async (uid, action) => {
    await api.post(`/admin/reports/${uid}/${action}`);
    toast.success(action === "suspend" ? "Member suspended" : action === "unsuspend" ? "Member reinstated" : "Reports dismissed");
    if (action === "unsuspend") setReports((r) => r.map((x) => (x.user.id === uid ? { ...x, user: { ...x.user, suspended: false } } : x)));
    else setReports((r) => r.filter((x) => x.user.id !== uid));
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/users/${id}/detail`);
      setDetail(data);
    } catch {
      toast.error("Could not load member details");
    }
  };

  const sendAdminMessage = async (body, audience, schedule) => {
    const t = msgTarget;
    try {
      if (t.broadcast && schedule) {
        const { data } = await api.post("/admin/broadcast/schedule", { body, audience, ...schedule });
        toast.success(`Scheduled for ${new Date(data.send_at).toLocaleString()}${data.repeat !== "none" ? ` · repeats ${data.repeat}` : ""}`);
        setSchedKey((k) => k + 1); setTab("broadcasts");
      } else if (t.broadcast) {
        const { data } = await api.post("/admin/broadcast", { body, audience });
        toast.success(`Broadcast delivered to ${data.sent} member${data.sent === 1 ? "" : "s"}`);
      } else {
        await api.post(`/admin/users/${t.id}/message`, { body });
        toast.success(`Message sent to ${t.name}`);
      }
      setMsgTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    }
  };

  const setIdentity = async (u, approved) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/identity?approved=${approved}`);
      setUserList((l) => l.map((x) => (x.id === u.id ? data : x)));
      toast.success(approved ? `${u.display_name} is now ID-verified` : `Identity approval revoked for ${u.display_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const revokeComp = async (u) => {
    try {
      const { data } = await api.delete(`/admin/complimentary/${u.id}`);
      setUserList((l) => l.map((x) => (x.id === u.id ? data : x)));
      toast.success(`Complimentary access ended for ${u.display_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke");
    }
  };

  const doWarn = async (reason) => {
    const t = warnTarget;
    setWarnTarget(null);
    try {
      if (t.bulk) {
        const { data } = await api.post("/admin/moderation/bulk", { user_ids: t.ids, action: "warn", reason });
        toast.success(`Warned ${data.affected} member${data.affected === 1 ? "" : "s"}`);
        setSelected(new Set()); loadReports();
      } else {
        await api.post(`/admin/users/${t.id}/warn`, { reason });
        toast.success(`Warning sent to ${t.name}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send warning");
    }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => (s.size === reports.length ? new Set() : new Set(reports.map((r) => r.user.id))));
  const bulkAction = async (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (action === "warn") { setWarnTarget({ bulk: true, name: `${ids.length} member${ids.length === 1 ? "" : "s"}`, ids }); return; }
    try {
      const { data } = await api.post("/admin/moderation/bulk", { user_ids: ids, action });
      toast.success(`${action === "suspend" ? "Suspended" : "Dismissed reports for"} ${data.affected} member${data.affected === 1 ? "" : "s"}`);
      setSelected(new Set()); loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  const assignTicket = async (ticket) => {
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/assign`);
      setTickets((list) => list.map((t) => (t.id === ticket.id ? data : t)));
      toast.success(data.assignee_id ? "Assigned to you" : "Released to the queue");
    } catch (err) {
      toast.error("Could not assign ticket");
    }
  };

  const sendReply = async (ticket) => {
    if (!replyText.trim()) return;
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/reply`, { message: replyText });
      setTickets((list) => list.map((t) => (t.id === ticket.id ? data : t)));
      setReplyText(""); setReplyFor(null);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reply");
    }
  };
  const setTicketStatusFn = async (ticket, status) => {
    await api.post(`/admin/tickets/${ticket.id}/status`, null, { params: { status } });
    setTickets((list) => list.map((t) => (t.id === ticket.id ? { ...t, status } : t)));
  };

  const setEv = (k, v) => setEvForm((f) => ({ ...f, [k]: v }));
  const createEvent = async () => {
    if (!evForm.title || !evForm.description || !evForm.location || !evForm.starts_at) {
      toast.error("Fill in title, description, location and date"); return;
    }
    setSavingEv(true);
    try {
      await api.post("/admin/events", {
        title: evForm.title, description: evForm.description, location: evForm.location,
        starts_at: new Date(evForm.starts_at).toISOString(), cover_url: evForm.cover_url || "",
        capacity: evForm.capacity ? parseInt(evForm.capacity, 10) : 0,
      });
      toast.success("Event published"); setEvForm(emptyEv); loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create event");
    } finally { setSavingEv(false); }
  };
  const delEvent = async (id) => {
    await api.delete(`/admin/events/${id}`);
    toast.success("Event removed");
    setEvents((e) => e.filter((x) => x.id !== id));
  };

  const Tab = ({ id, label, icon: Icon, badge }) => (
    <button data-testid={`tab-${id}`} onClick={() => setTab(id)}
      className={`flex items-center gap-2 text-sm tracking-wide pb-2 border-b-2 transition-colors whitespace-nowrap ${tab === id ? "text-text-main border-wine" : "text-text-muted border-transparent hover:text-text-main"}`}>
      <Icon size={15} /> {label}
      {badge > 0 && <span className="ml-1 text-[10px] bg-wine text-white rounded-full px-1.5 py-0.5 leading-none">{badge}</span>}
    </button>
  );

  const s = overview?.stats;

  return (
    <div className="pt-24 pb-24 max-w-6xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Concierge</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <h1 className="font-display text-6xl font-light">Admin Dashboard</h1>
          <button data-testid="broadcast-btn" onClick={() => setMsgTarget({ broadcast: true })}
            className="flex items-center gap-2 px-6 py-3 rounded-full border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors">
            <Radio size={14} /> Broadcast
          </button>
        </div>
        <div className="flex gap-6 mb-12 border-b border-white/8 overflow-x-auto pb-px">
          <Tab id="overview" label="Overview" icon={LayoutDashboard} />
          <Tab id="tickets" label="Support" icon={LifeBuoy} badge={s?.open_tickets} />
          <Tab id="users" label="Users" icon={UserCog} />
          <Tab id="models" label="Models" icon={Crown} />
          <Tab id="reports" label="Moderation" icon={Flag} badge={s?.open_reports} />
          <Tab id="verifications" label="Verifications" icon={BadgeCheck} badge={s?.pending_verifications} />
          <Tab id="events" label="Events" icon={CalendarHeart} />
          <Tab id="broadcasts" label="Broadcasts" icon={Radio} />
          <Tab id="applications" label="Applications" icon={Crown} badge={s?.pending_applications} />
          <Tab id="socials" label="Socials" icon={Share2} />
          <Tab id="emails" label="Emails" icon={Mail} />
          <Tab id="ads" label="My Ads" icon={Sparkles} />
          <Tab id="marketing" label="Marketing" icon={Megaphone} />
          <Tab id="refunds" label="Refunds" icon={RotateCcw} />
          <Tab id="audit" label="Audit" icon={ScrollText} />
        </div>
      </motion.div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        !overview ? <p className="text-text-muted">Loading…</p> : (
          <div className="space-y-10" data-testid="overview-panel">
            <StripeStatus />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat icon={Users} label="Total members" value={s.total_users} accent="bg-white/10 text-text-main" />
              <Stat icon={UserPlus} label="New this week" value={s.new_registrations_7d} accent="bg-emerald-500/15 text-emerald-300" />
              <Stat icon={Sparkles} label="Premium" value={s.premium_users} accent="bg-gold/15 text-gold" />
              <Stat icon={BadgeCheck} label="Basic" value={s.basic_users} accent="bg-emerald-500/15 text-emerald-300" />
              <Stat icon={ShieldCheck} label="ID verified" value={s.verified_users} accent="bg-gold/15 text-gold" />
              <Stat icon={Ticket} label="Open tickets" value={s.open_tickets} accent="bg-wine/25 text-gold" />
              <Stat icon={Flag} label="Open reports" value={s.open_reports} accent="bg-red-500/15 text-red-300" />
              <Stat icon={CalendarHeart} label="Active events" value={s.active_events} accent="bg-wine/25 text-gold" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-white/8 bg-surface p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-5 flex items-center gap-2"><TrendingUp size={14} /> Recent registrations</p>
                {overview.recent_registrations.length === 0 ? <p className="text-text-muted text-sm">No members yet.</p> :
                 <div className="space-y-3">
                   {overview.recent_registrations.map((u) => (
                     <div key={u.id} className="flex items-center gap-3">
                       <img src={u.photo_url || FALLBACK} alt="" className="w-9 h-9 rounded-full object-cover" />
                       <span className="flex-1 text-sm">{u.display_name}</span>
                       <span className={`text-[9px] uppercase tracking-[0.12em] border px-2 py-0.5 rounded-full ${TIER_STYLE[u.membership_tier]}`}>{u.membership_tier}</span>
                     </div>
                   ))}
                 </div>}
              </div>
              <div className="rounded-2xl border border-white/8 bg-surface p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-text-muted mb-5 flex items-center gap-2"><CalendarHeart size={14} /> Upcoming events</p>
                {overview.upcoming_events.length === 0 ? <p className="text-text-muted text-sm">No upcoming events.</p> :
                 <div className="space-y-3">
                   {overview.upcoming_events.map((e) => (
                     <div key={e.id} className="flex items-center justify-between gap-3">
                       <div className="min-w-0"><p className="text-sm truncate">{e.title}</p><p className="text-xs text-text-muted">{e.location} · {new Date(e.starts_at).toLocaleDateString()}</p></div>
                       <span className="text-xs text-text-muted flex items-center gap-1 shrink-0"><Users size={12} /> {e.rsvp_count}</span>
                     </div>
                   ))}
                 </div>}
              </div>
            </div>
          </div>
        )
      )}

      {/* SUPPORT TICKETS */}
      {tab === "tickets" && (
        <div className="space-y-6" data-testid="tickets-panel">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[200px] border-b border-white/15 py-2">
              <Search size={15} className="text-text-muted" />
              <input data-testid="ticket-search" value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadTickets()} placeholder="Search subject, message or member…"
                className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            {["", "open", "in_progress", "answered", "closed"].map((st) => (
              <button key={st || "all"} data-testid={`ticket-filter-${st || "all"}`} onClick={() => { setTicketStatus(st); loadTickets({ status: st }); }}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] border transition-colors ${ticketStatus === st ? "bg-wine border-wine text-white" : "border-white/15 text-text-muted hover:text-text-main"}`}>
                {st ? st.replace("_", " ") : "all"}
              </button>
            ))}
            <button data-testid="ticket-search-btn" onClick={loadTickets} className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] bg-gold text-ink">Search</button>
          </div>
          {!tickets ? <p className="text-text-muted">Loading…</p> :
           tickets.length === 0 ? <p data-testid="no-tickets" className="text-text-muted font-display text-2xl italic">No tickets found.</p> :
           <div className="space-y-4">
             {tickets.map((t) => (
               <div key={t.id} data-testid={`ticket-${t.id}`} className="rounded-2xl border border-white/8 bg-surface p-5">
                 <div className="flex items-start justify-between gap-4">
                   <div className="min-w-0">
                     <div className="flex items-center gap-3 flex-wrap">
                       <h3 className="text-lg">{t.subject}</h3>
                       <span className="text-[9px] uppercase tracking-[0.12em] border border-white/15 text-text-muted px-2 py-0.5 rounded-full">{t.category}</span>
                       <span className={`text-[9px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border ${t.status === "open" ? "text-amber-300 border-amber-500/40" : t.status === "in_progress" ? "text-sky-300 border-sky-500/40" : t.status === "answered" ? "text-emerald-300 border-emerald-500/40" : "text-text-muted border-white/15"}`}>{t.status.replace("_", " ")}</span>
                       {t.assignee_id && <span data-testid={`ticket-assignee-${t.id}`} className="text-[9px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border border-wine/40 text-gold">◆ {t.assignee_name}</span>}
                     </div>
                     <p className="text-xs text-text-muted mt-1">{t.user_name} · {t.user_email} · {new Date(t.created_at).toLocaleString()}</p>
                     <p className="text-sm mt-3">{t.message}</p>
                     {t.replies?.map((r, i) => (
                       <div key={i} className="mt-3 rounded-xl bg-wine/10 border border-wine/30 p-3">
                         <p className="text-[10px] uppercase tracking-[0.15em] text-gold mb-1">{r.admin_name}</p>
                         <p className="text-sm">{r.message}</p>
                       </div>
                     ))}
                   </div>
                   <div className="flex flex-col gap-2 shrink-0">
                     <button data-testid={`ticket-assign-${t.id}`} onClick={() => assignTicket(t)}
                       className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] transition-colors ${t.assignee_id === user.id ? "bg-wine/20 border border-wine/50 text-gold" : "border border-white/15 text-text-muted hover:text-text-main"}`}>
                       <UserCheck size={13} /> {t.assignee_id === user.id ? "Assigned to you" : t.assignee_id ? "Reassign to me" : "Assign to me"}
                     </button>
                     <button data-testid={`ticket-reply-btn-${t.id}`} onClick={() => { setReplyFor(replyFor === t.id ? null : t.id); setReplyText(""); }}
                       className="flex items-center gap-2 px-4 py-2 rounded-full bg-wine hover:bg-wine-hover transition-colors text-xs uppercase tracking-[0.12em]"><Send size={13} /> Reply</button>
                     {t.status !== "closed" && <button data-testid={`ticket-close-${t.id}`} onClick={() => setTicketStatusFn(t, "closed")} className="px-4 py-2 rounded-full border border-white/15 text-xs uppercase tracking-[0.12em] text-text-muted hover:text-text-main transition-colors">Close</button>}
                   </div>
                 </div>
                 {replyFor === t.id && (
                   <div className="mt-4 flex gap-3">
                     <input data-testid={`ticket-reply-input-${t.id}`} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && sendReply(t)} placeholder="Type your reply…" autoFocus
                       className="flex-1 bg-transparent border-b border-white/15 py-2 focus:border-wine outline-none transition-colors text-sm" />
                     <button data-testid={`ticket-reply-send-${t.id}`} onClick={() => sendReply(t)} className="w-10 h-10 rounded-full bg-gold text-ink flex items-center justify-center"><Send size={15} /></button>
                   </div>
                 )}
               </div>
             ))}
           </div>}
        </div>
      )}

      {/* USER MANAGEMENT */}
      {tab === "users" && (
        <div className="space-y-6" data-testid="users-panel">
          <CompForm onGranted={loadUsers} />
          <div className="flex items-center gap-2 border-b border-white/15 py-2 max-w-md">
            <Search size={15} className="text-text-muted" />
            <input data-testid="user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadUsers()} placeholder="Search by name, email or location…"
              className="flex-1 bg-transparent outline-none text-sm" />
            <button data-testid="user-search-btn" onClick={loadUsers} className="text-xs uppercase tracking-[0.12em] text-gold">Search</button>
          </div>
          {!userList ? <p className="text-text-muted">Loading…</p> :
           userList.length === 0 ? <p data-testid="no-users" className="text-text-muted italic">No members found.</p> :
           <div className="border border-white/8 rounded-2xl bg-surface overflow-hidden">
             <div className="hidden md:grid grid-cols-[1fr_150px_100px_260px] gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-text-muted border-b border-white/8">
               <span>Member</span><span>Tier</span><span>Verified</span><span>Actions</span>
             </div>
             <div className="divide-y divide-white/5">
               {userList.map((u) => (
                 <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-1 md:grid-cols-[1fr_150px_100px_260px] gap-3 md:gap-4 px-5 py-4 items-center">
                   <div className="flex items-center gap-3 min-w-0">
                     <img src={u.photo_url || FALLBACK} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                     <div className="min-w-0">
                       <p className="text-sm truncate flex items-center gap-2">{u.display_name}
                         {u.role === "admin" && <span className="text-[9px] uppercase text-gold border border-wine/40 px-1.5 rounded">admin</span>}
                         {u.is_creator && <span className="text-[9px] uppercase text-gold border border-wine/40 px-1.5 rounded">creator</span>}
                         {u.suspended && <span className="text-[9px] uppercase text-amber-300 border border-amber-500/40 px-1.5 rounded">suspended</span>}
                       </p>
                       <p className="text-xs text-text-muted truncate">{u.email}</p>
                     </div>
                   </div>
                   <div className="flex flex-col gap-1 items-start">
                     <span className={`text-[10px] uppercase tracking-[0.12em] border px-2.5 py-1 rounded-full w-fit ${TIER_STYLE[u.membership_tier]}`}>{u.membership_tier}</span>
                     {u.complimentary && (
                       <span data-testid={`user-comp-${u.id}`} title={`Granted by ${u.complimentary.granted_by_name}`} className="text-[9px] uppercase tracking-[0.12em] text-gold flex items-center gap-1">
                         <Crown size={10} /> Comp · {u.complimentary.until ? `until ${new Date(u.complimentary.until).toLocaleDateString()}` : "unlimited"}
                       </span>
                     )}
                   </div>
                   <span data-testid={`user-verified-${u.id}`} className="text-xs">
                     {u.age_verified ? <span className="text-gold flex items-center gap-1"><ShieldCheck size={13} /> 18+</span> : <span className="text-text-muted">Unverified</span>}
                   </span>
                   <div className="flex flex-wrap gap-2">
                     <button data-testid={`user-detail-${u.id}`} onClick={() => openDetail(u.id)}
                       className="flex items-center gap-1 px-3 py-2 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.12em] hover:text-text-main transition-colors"><Eye size={12} /> View</button>
                     {u.role !== "admin" && (
                       <button data-testid={`user-identity-${u.id}`} onClick={() => setIdentity(u, !u.age_verified)} title={u.age_verified ? "Revoke identity approval" : "Manually approve identity (18+)"}
                         className={`flex items-center gap-1 px-3 py-2 rounded-full border text-[10px] uppercase tracking-[0.12em] transition-colors ${u.age_verified ? "border-white/15 text-text-muted hover:text-amber-300" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>
                         <ShieldCheck size={12} /> {u.age_verified ? "Revoke ID" : "Approve ID"}</button>
                     )}
                     {u.role !== "admin" && (
                       <button data-testid={`user-message-${u.id}`} onClick={() => setMsgTarget({ id: u.id, name: u.display_name })}
                         className="flex items-center gap-1 px-3 py-2 rounded-full border border-gold/40 text-gold text-[10px] uppercase tracking-[0.12em] hover:bg-gold/10 transition-colors"><MessageSquare size={12} /> Message</button>
                     )}
                     <button data-testid={`user-warn-${u.id}`} onClick={() => setWarnTarget({ id: u.id, name: u.display_name })}
                       className="flex items-center gap-1 px-3 py-2 rounded-full border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-[0.12em] hover:bg-amber-500/10 transition-colors"><ShieldAlert size={12} /> Warn</button>
                     {u.complimentary && (
                       <button data-testid={`user-revoke-comp-${u.id}`} onClick={() => revokeComp(u)} title="End complimentary membership"
                         className="flex items-center gap-1 px-3 py-2 rounded-full border border-gold/30 text-text-muted text-[10px] uppercase tracking-[0.12em] hover:text-gold transition-colors"><X size={12} /> Comp</button>
                     )}
                     {u.suspended ? (
                       <button data-testid={`user-unsuspend-${u.id}`} onClick={() => api.post(`/admin/reports/${u.id}/unsuspend`).then(() => { toast.success("Reinstated"); loadUsers(); })}
                         className="px-3 py-2 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.12em]"><RotateCcw size={12} /></button>
                     ) : u.role !== "admin" && (
                       <button data-testid={`user-suspend-${u.id}`} onClick={() => api.post(`/admin/reports/${u.id}/suspend`).then(() => { toast.success("Suspended"); loadUsers(); })}
                         className="px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[10px] uppercase tracking-[0.12em]"><Ban size={12} /></button>
                     )}
                   </div>
                 </div>
               ))}
             </div>
           </div>}
        </div>
      )}

      {/* MODERATION / REPORTS */}
      {tab === "reports" && (
        !reports ? <p className="text-text-muted">Loading…</p> :
        reports.length === 0 ? <p data-testid="no-reports" className="text-text-muted font-display text-2xl italic">No open reports. All calm.</p> :
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/8 bg-surface px-5 py-3">
            <button data-testid="select-all-reports" onClick={toggleAll} className="flex items-center gap-2 text-sm text-text-muted hover:text-text-main transition-colors">
              {selected.size === reports.length ? <CheckSquare size={16} className="text-gold" /> : <Square size={16} />}
              {selected.size > 0 ? `${selected.size} selected` : "Select all"}
            </button>
            {selected.size > 0 && (
              <div data-testid="bulk-bar" className="flex gap-2">
                <button data-testid="bulk-warn" onClick={() => bulkAction("warn")} className="flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors text-xs uppercase tracking-[0.12em]"><ShieldAlert size={13} /> Warn ({selected.size})</button>
                <button data-testid="bulk-suspend" onClick={() => bulkAction("suspend")} className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors text-xs uppercase tracking-[0.12em]"><Ban size={13} /> Suspend ({selected.size})</button>
                <button data-testid="bulk-dismiss" onClick={() => bulkAction("dismiss")} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-text-muted hover:text-text-main transition-colors text-xs uppercase tracking-[0.12em]"><X size={13} /> Dismiss ({selected.size})</button>
              </div>
            )}
          </div>
          {reports.map((r) => (
            <div key={r.user.id} data-testid={`report-${r.user.id}`} className={`flex flex-col md:flex-row md:items-center gap-5 rounded-2xl border bg-surface p-5 transition-colors ${selected.has(r.user.id) ? "border-wine/50" : "border-white/8"}`}>
              <button data-testid={`report-select-${r.user.id}`} onClick={() => toggleSelect(r.user.id)} className="shrink-0 text-text-muted hover:text-gold transition-colors">
                {selected.has(r.user.id) ? <CheckSquare size={20} className="text-gold" /> : <Square size={20} />}
              </button>
              <img src={r.user.photo_url || FALLBACK} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display text-2xl leading-none">{r.user.display_name}<span className="text-text-muted text-base">, {r.user.age}</span></h3>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-red-300 border border-red-500/40 px-2.5 py-1 rounded-full">{r.count} report{r.count > 1 ? "s" : ""}</span>
                  {r.user.suspended && <span className="text-[10px] uppercase tracking-[0.15em] text-amber-300 border border-amber-500/40 px-2.5 py-1 rounded-full">Suspended</span>}
                </div>
                <p className="text-xs text-text-muted mt-2">Reasons: {r.reasons.map((x) => x.reason).filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <div className="flex gap-3 shrink-0 flex-wrap">
                <button data-testid={`warn-${r.user.id}`} onClick={() => setWarnTarget({ id: r.user.id, name: r.user.display_name })}
                  className="flex items-center gap-2 px-5 py-3 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors text-xs uppercase tracking-[0.15em]"><ShieldAlert size={14} /> Warn</button>
                {r.user.suspended ? (
                  <button data-testid={`unsuspend-${r.user.id}`} onClick={() => actReport(r.user.id, "unsuspend")} className="flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-emerald-500/50 transition-colors text-xs uppercase tracking-[0.15em] text-text-muted"><RotateCcw size={14} /> Reinstate</button>
                ) : (
                  <button data-testid={`suspend-${r.user.id}`} onClick={() => actReport(r.user.id, "suspend")} className="flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors text-xs uppercase tracking-[0.15em]"><Ban size={14} /> Suspend</button>
                )}
                <button data-testid={`dismiss-${r.user.id}`} onClick={() => actReport(r.user.id, "dismiss")} className="flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-white/30 transition-colors text-xs uppercase tracking-[0.15em] text-text-muted"><X size={14} /> Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* VERIFICATIONS */}
      {tab === "verifications" && <AgeReviews />}
      {tab === "verifications" && (
        !reqs ? <p className="text-text-muted">Loading…</p> :
        reqs.length === 0 ? <p data-testid="no-pending" className="text-text-muted font-display text-2xl italic">No pending verifications.</p> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reqs.map((r) => (
            <div key={r.id} data-testid={`verify-req-${r.id}`} className="rounded-2xl border border-white/8 bg-surface overflow-hidden">
              <div className="grid grid-cols-2">
                <div className="relative aspect-square overflow-hidden border-r border-white/8">
                  <img src={r.selfie_url} alt="selfie" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.15em] bg-black/60 px-2 py-1 rounded">Selfie</span>
                </div>
                <div className="relative aspect-square overflow-hidden">
                  <img src={r.user.photo_url || FALLBACK} alt="profile" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.15em] bg-black/60 px-2 py-1 rounded">Profile</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display text-2xl leading-none">{r.user.display_name}<span className="text-text-muted text-lg">, {r.user.age}</span></h3>
                {r.user.location && <p className="flex items-center gap-1 text-xs text-text-muted mt-2"><MapPin size={12} /> {r.user.location}</p>}
                <div className="flex gap-3 mt-5">
                  <button data-testid={`approve-${r.id}`} onClick={() => actVerify(r.id, "approve")} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors text-xs uppercase tracking-[0.15em]"><BadgeCheck size={15} /> Approve</button>
                  <button data-testid={`reject-${r.id}`} onClick={() => actVerify(r.id, "reject")} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full border border-white/15 hover:border-red-500/50 transition-colors text-xs uppercase tracking-[0.15em] text-text-muted"><X size={15} /> Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EVENTS */}
      {tab === "events" && (
        <div className="space-y-10">
          <div className="rounded-2xl border border-white/8 bg-surface p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-5 flex items-center gap-2"><Plus size={14} /> Create an event</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="event-title" value={evForm.title} onChange={(e) => setEv("title", e.target.value)} placeholder="Title" className="bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors" />
              <input data-testid="event-location" value={evForm.location} onChange={(e) => setEv("location", e.target.value)} placeholder="Location" className="bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors" />
              <input data-testid="event-date" type="datetime-local" value={evForm.starts_at} onChange={(e) => setEv("starts_at", e.target.value)} className="bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors text-text-muted" />
              <input data-testid="event-capacity" type="number" min="0" value={evForm.capacity} onChange={(e) => setEv("capacity", e.target.value)} placeholder="Capacity (0 = unlimited)" className="bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors" />
              <input data-testid="event-cover" value={evForm.cover_url} onChange={(e) => setEv("cover_url", e.target.value)} placeholder="Cover image URL (optional)" className="sm:col-span-2 bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors" />
              <textarea data-testid="event-description" value={evForm.description} onChange={(e) => setEv("description", e.target.value)} placeholder="Description" rows={3} className="sm:col-span-2 bg-transparent border border-white/15 rounded-xl p-3 focus:border-wine outline-none transition-colors" />
            </div>
            <button data-testid="event-create-btn" onClick={createEvent} disabled={savingEv}
              className="mt-6 px-8 py-3 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors disabled:opacity-50">
              {savingEv ? "Publishing…" : "Publish event"}
            </button>
          </div>
          {!events ? <p className="text-text-muted">Loading…</p> :
           events.length === 0 ? <p data-testid="no-events" className="text-text-muted font-display text-2xl italic">No events yet.</p> :
           <div className="space-y-4">
             {events.map((ev) => (
               <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-5 rounded-2xl border border-white/8 bg-surface p-5">
                 <img src={ev.cover_url || FALLBACK} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
                 <div className="flex-1 min-w-0">
                   <h3 className="font-display text-2xl leading-none">{ev.title}</h3>
                   <p className="text-xs text-text-muted mt-2 flex items-center gap-3">
                     <span className="flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>
                     <span className="flex items-center gap-1"><Users size={12} /> {ev.rsvp_count} going</span>
                   </p>
                 </div>
                 <button data-testid={`event-delete-${ev.id}`} onClick={() => delEvent(ev.id)}
                   className="flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-red-500/50 transition-colors text-xs uppercase tracking-[0.15em] text-text-muted"><Trash2 size={14} /> Remove</button>
               </div>
             ))}
           </div>}
        </div>
      )}

      {/* AUDIT */}
      {tab === "broadcasts" && <ScheduledBroadcasts refreshKey={schedKey} />}
      {tab === "applications" && <CreatorApplications />}
      {tab === "socials" && <SocialHub />}
      {tab === "emails" && <EmailPreviews />}
      {tab === "ads" && <MyAds onManageSocials={() => setTab("socials")} />}
      {tab === "marketing" && <MarketingAdmin />}
      {tab === "models" && <ModelsList onOpen={openDetail} />}
      {tab === "refunds" && <WalletReversals />}

      {tab === "audit" && (
        !audit ? <p className="text-text-muted">Loading…</p> :
        audit.length === 0 ? <p data-testid="no-audit" className="text-text-muted font-display text-2xl italic">No actions logged yet.</p> :
        <div className="border border-white/8 rounded-2xl bg-surface divide-y divide-white/5">
          {audit.map((a) => {
            const labels = { verification_approved: "approved verification for", verification_rejected: "rejected verification for", member_suspended: "suspended", member_reinstated: "reinstated", reports_dismissed: "dismissed reports for", member_warned: "warned", ticket_replied: "replied to a ticket from", complimentary_granted: "granted complimentary membership to", complimentary_revoked: "ended complimentary membership for", admin_message_sent: "messaged", broadcast_sent: "sent a broadcast to", broadcast_scheduled: "scheduled a broadcast for", broadcast_cancelled: "cancelled a scheduled broadcast for", identity_approved: "manually approved identity for", identity_revoked: "revoked identity approval for" };
            return (
              <div key={a.id} data-testid={`audit-${a.id}`} className="flex items-center justify-between gap-4 px-5 py-4">
                <p className="text-sm"><span className="text-gold">{a.admin_name}</span> <span className="text-text-muted">{labels[a.action] || a.action}</span> <span className="text-text-main">{a.target_name}</span></p>
                <span className="text-xs text-text-muted shrink-0">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}

      <WarnModal open={!!warnTarget} name={warnTarget?.name} onClose={() => setWarnTarget(null)} onConfirm={doWarn} />
      <MessageModal target={msgTarget} onClose={() => setMsgTarget(null)} onSend={sendAdminMessage} />
      <UserDetailModal data={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
